import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCatalog } from "@/lib/useCatalog";
import { Button } from "@/components/ui/button";
import { CategoryIcon } from "@/components/CategoryIcon";
import { CitySelect } from "@/components/CitySelect";
import { ProviderCard } from "@/components/ProviderCard";
import { PageLoader } from "@/components/layout/RequireAuth";
import { PackageCard } from "@/components/PackageCard";

export const EmptyState = ({ title, desc, action, testId = "empty-state" }) => (
  <div className="rounded-2xl border border-dashed border-stone-300 bg-white/60 px-6 py-14 text-center" data-testid={testId}>
    <p className="font-semibold text-stone-800">{title}</p>
    {desc && <p className="mx-auto mt-1 max-w-md text-sm text-stone-500">{desc}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cities } = useCatalog();
  const [cat, setCat] = useState(null);
  const [city, setCity] = useState("");
  const [providers, setProviders] = useState(null);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    setCat(null);
    api.get(`/categories/${slug}`).then((r) => setCat(r.data)).catch(() => navigate("/"));
  }, [slug, navigate]);

  useEffect(() => {
    setProviders(null);
    api.get("/providers", { params: { category: slug, city: city || undefined } }).then((r) => setProviders(r.data));
    api.get("/packages", { params: { category: slug, city: city || undefined } }).then((r) => setPackages(r.data));
  }, [slug, city]);

  if (!cat) return <PageLoader />;
  const ctaTarget = `/minta-penawaran?kategori=${slug}`;
  const ctaHref = user ? ctaTarget : `/masuk?next=${encodeURIComponent(ctaTarget)}`;
  const isProvider = user && user.role !== "pelanggan";

  return (
    <div data-testid="category-page">
      <section className="border-b border-stone-200 bg-white">
        <div className="container-page grid gap-10 py-12 lg:grid-cols-[1.4fr_1fr] lg:py-16">
          <div className="animate-fade-up">
            <nav className="flex items-center gap-1 text-xs text-stone-500" data-testid="category-breadcrumb">
              <Link to="/" className="hover:text-teal-700">Beranda</Link> <ChevronRight className="h-3 w-3" />
              <span>{cat.group?.name}</span> <ChevronRight className="h-3 w-3" />
              <span className="text-stone-800">{cat.name}</span>
            </nav>
            <div className="mt-6 flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700"><CategoryIcon name={cat.icon} className="h-7 w-7" /></span>
              <h1 className="text-4xl font-extrabold tracking-tight text-stone-900 sm:text-5xl" data-testid="category-title">{cat.name}</h1>
            </div>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg" data-testid="category-description">{cat.description}</p>
            {!isProvider && (
              <Button asChild className="mt-8 h-12 rounded-full bg-teal-700 px-7 text-base font-semibold hover:bg-teal-800 active:scale-[0.98]" data-testid="category-request-cta">
                <Link to={ctaHref}>Minta Penawaran Gratis <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            )}
          </div>
          <div className="card-soft self-start p-6">
            <p className="text-sm font-bold text-stone-900">Yang akan kami tanyakan</p>
            <ul className="mt-4 space-y-3">
              {cat.questions.map((q) => (
                <li key={q.key} className="flex items-start gap-2.5 text-sm text-stone-600">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> {q.label}
                </li>
              ))}
              <li className="flex items-start gap-2.5 text-sm text-stone-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> Lokasi, jadwal, dan foto (opsional)</li>
            </ul>
          </div>
        </div>
      </section>
      <section className="container-page pt-12" data-testid="category-packages-section">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-stone-900">Paket Harga Tetap</h2>
            <p className="mt-1 text-sm text-stone-500">Pesan langsung dengan harga pasti, tanpa menunggu penawaran.</p>
          </div>
          <div className="sm:w-56"><CitySelect cities={cities} value={city} onChange={setCity} testId="category-package-city-filter" /></div>
        </div>
        <div className="mt-6">
          {packages.length === 0 ? <EmptyState testId="category-packages-empty" title="Belum ada paket di kota ini" /> : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="category-package-list">
              {packages.map((pk) => <PackageCard key={pk.package_id} pkg={pk} showProvider canBook={!isProvider} />)}
            </div>
          )}
        </div>
      </section>
      <section className="container-page py-12">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <h2 className="text-2xl font-bold tracking-tight text-stone-900">Penyedia {cat.name}</h2>
          <div className="sm:w-56"><CitySelect cities={cities} value={city} onChange={setCity} testId="category-city-filter" /></div>
        </div>
        <div className="mt-8">
          {providers === null ? <PageLoader /> : providers.length === 0 ? (
            <EmptyState testId="category-empty" title="Belum ada penyedia di kota ini" desc="Kirim permintaan saja — penyedia baru di area Anda akan melihatnya." />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-provider-list">
              {providers.map((p) => <ProviderCard key={p.slug} provider={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
